import React, { useState, useMemo } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-dist-min';
import { SAIFileResponse, VisualizeResponse, trainedModelsApi } from '../services/api';

const Plot = createPlotlyComponent(Plotly);

interface ParameterVisualizationProps {
  selectedSAIFile: SAIFileResponse;
  modelName: string;
  onBack: () => void;
}

export const ParameterVisualization: React.FC<ParameterVisualizationProps> = ({
  selectedSAIFile,
  modelName,
  onBack,
}) => {
  const [selectedParams, setSelectedParams] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<VisualizeResponse | null>(null);

  // Filter to input parameters only
  const inputParams = useMemo(
    () => (selectedSAIFile.parameters || []).filter((p: any) => (p.type || '').toLowerCase() === 'input'),
    [selectedSAIFile.parameters]
  );

  const handleToggleParam = (name: string) => {
    setSelectedParams(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else if (next.size < 2) {
        next.add(name);
      }
      return next;
    });
  };

  // Editable fixed values for non-selected input params
  const [fixedValues, setFixedValues] = useState<Record<string, number>>(() => {
    const fixed: Record<string, number> = {};
    (selectedSAIFile.parameters || [])
      .filter((p: any) => (p.type || '').toLowerCase() === 'input')
      .forEach((p: any) => {
        fixed[p.name] = p.default ?? 0;
      });
    return fixed;
  });

  // Filtered fixed values (only non-selected params)
  const visibleFixedValues = useMemo(() => {
    const visible: Record<string, number> = {};
    Object.entries(fixedValues).forEach(([name, val]) => {
      if (!selectedParams.has(name)) {
        visible[name] = val;
      }
    });
    return visible;
  }, [fixedValues, selectedParams]);

  const handleGenerate = async () => {
    const trainedModel = selectedSAIFile.trained_models?.find(
      (m: any) => m.name === modelName
    );
    if (trainedModel?.id == null) {
      setError('Model not found');
      return;
    }

    // Resolution based on number of selected params
    const count = selectedParams.size;
    const resolution = count === 1 ? 200 : 100;

    setIsLoading(true);
    setError(null);
    try {
      const data = await trainedModelsApi.visualize(
        trainedModel.id,
        Array.from(selectedParams),
        visibleFixedValues,
        resolution
      );
      setResult(data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Visualization failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Render the Plotly chart based on number of selected params
  const renderPlot = () => {
    if (!result) return null;

    const paramNames = Object.keys(result.axes);
    const count = paramNames.length;
    const res = result.resolution;
    const outputLabel = `${result.output.name}${result.output.unit ? ` (${result.output.unit})` : ''}`;

    if (count === 1) {
      const xName = paramNames[0];
      const xParam = inputParams.find((p: any) => p.name === xName);
      const xLabel = xParam?.label || xName;
      return (
        <Plot
          data={[{
            x: result.axes[xName],
            y: result.output.values,
            type: 'scatter',
            mode: 'lines',
            line: { color: '#103097', width: 2 },
          }]}
          layout={{
            title: `${outputLabel} vs ${xLabel}`,
            xaxis: { title: `${xLabel}${xParam?.unit ? ` (${xParam.unit})` : ''}` },
            yaxis: { title: outputLabel },
            autosize: true,
            margin: { t: 50, b: 60, l: 70, r: 30 },
          }}
          useResizeHandler
          style={{ width: '100%', height: '500px' }}
        />
      );
    }

    if (count === 2) {
      const xName = paramNames[0];
      const zName = paramNames[1];
      const xParam = inputParams.find((p: any) => p.name === xName);
      const zParam = inputParams.find((p: any) => p.name === zName);
      const xLabel = xParam?.label || xName;
      const zLabel = zParam?.label || zName;

      // Reshape flat values into 2D grid (res x res)
      const zValues: number[][] = [];
      for (let i = 0; i < res; i++) {
        zValues.push(result.output.values.slice(i * res, (i + 1) * res));
      }

      return (
        <Plot
          data={[{
            x: result.axes[xName],
            y: result.axes[zName],
            z: zValues,
            type: 'surface',
            colorscale: 'Viridis',
            colorbar: { title: { text: outputLabel, side: 'right' }, len: 0.6 },
          }]}
          layout={{
            title: `${outputLabel}`,
            scene: {
              xaxis: {
                title: { text: `${xLabel}${xParam?.unit ? ` (${xParam.unit})` : ''}`, standoff: 15 },
                tickfont: { size: 10 },
              },
              yaxis: {
                title: { text: `${zLabel}${zParam?.unit ? ` (${zParam.unit})` : ''}`, standoff: 15 },
                tickfont: { size: 10 },
              },
              zaxis: {
                title: { text: outputLabel, standoff: 15 },
                tickfont: { size: 10 },
              },
              camera: { eye: { x: 1.6, y: 1.6, z: 1.0 } },
              aspectmode: 'cube' as any,
            },
            autosize: true,
            margin: { t: 50, b: 10, l: 10, r: 10 },
          }}
          useResizeHandler
          style={{ width: '100%', height: '600px' }}
        />
      );
    }

    return null;
  };

  return (
    <div className="wizard-step-container">
      <div className="wizard-step-header">
        <button className="back-button" onClick={onBack} disabled={isLoading}>
          <span className="back-arrow">←</span> Back
        </button>
        <h1>Visualize Response Surface</h1>
      </div>

      {/* Parameter Selection */}
      <p className="parameters-description">
        Select 1–2 input parameters to plot against the model output. Non-selected parameters are held at their default values.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
        {inputParams.map((p: any) => {
          const checked = selectedParams.has(p.name);
          const disabled = !checked && selectedParams.size >= 2;
          return (
            <label
              key={p.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                borderRadius: '6px',
                border: `1px solid ${checked ? 'var(--primary500, #103097)' : 'var(--gray300, #ccc)'}`,
                background: checked ? 'var(--primary50, #e8edf8)' : 'white',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                fontSize: '0.875rem',
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => handleToggleParam(p.name)}
              />
              {p.label || p.name}
              {p.unit ? ` (${p.unit})` : ''}
            </label>
          );
        })}
      </div>

      {/* Fixed Parameters Summary */}
      {selectedParams.size > 0 && Object.keys(visibleFixedValues).length > 0 && (
        <div style={{
          background: 'var(--gray50, #f9fafb)',
          border: '1px solid var(--gray200, #e5e7eb)',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          display: 'inline-block',
        }}>
          <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--gray700, #374151)', marginBottom: '10px' }}>
            Fixed Parameters
          </div>
          <table style={{ borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--gray200, #e5e7eb)' }}>
                <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--gray500, #6b7280)', fontWeight: 500 }}>Parameter</th>
                <th style={{ textAlign: 'left', padding: '6px 10px', color: 'var(--gray500, #6b7280)', fontWeight: 500, minWidth: '200px' }}></th>
                <th style={{ textAlign: 'right', padding: '6px 10px', color: 'var(--gray500, #6b7280)', fontWeight: 500 }}>Value</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(visibleFixedValues).map(([name, val]) => {
                const param = inputParams.find((p: any) => p.name === name);
                const min = param?.min ?? 0;
                const max = param?.max ?? 1;
                const step = (max - min) / 200;
                return (
                  <tr key={name} style={{ borderBottom: '1px solid var(--gray100, #f3f4f6)' }}>
                    <td style={{ padding: '6px 10px', color: 'var(--gray700, #374151)', whiteSpace: 'nowrap' }}>{param?.label || name}</td>
                    <td style={{ padding: '6px 10px' }}>
                      <input
                        type="range"
                        min={min}
                        max={max}
                        step={step}
                        value={val}
                        onChange={(e) => setFixedValues(prev => ({ ...prev, [name]: parseFloat(e.target.value) }))}
                        style={{ width: '100%', cursor: 'pointer', accentColor: 'var(--primary500, #103097)' }}
                      />
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--primary700, #103097)', whiteSpace: 'nowrap' }}>
                      {typeof val === 'number' ? val.toFixed(4) : val}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Generate Button */}
      <div style={{ marginBottom: '24px' }}>
        <button
          className="btn btn-primary"
          onClick={handleGenerate}
          disabled={isLoading || selectedParams.size === 0}
          style={{ minWidth: '160px' }}
        >
          {isLoading ? 'Generating...' : 'Generate Plot'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{ color: 'red', marginBottom: '16px' }}>{error}</div>
      )}

      {/* Plot */}
      {result && renderPlot()}
    </div>
  );
};
